import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Award } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

export default function OrderSuccess() {
  const [params] = useSearchParams();
  const { t } = useLanguage();
  const { clear } = useCart();
  const { refreshUser } = useAuth();
  const sessionId = params.get("session_id");
  const orderId = params.get("order_id");
  const cod = params.get("cod");

  const [status, setStatus] = useState(cod ? "paid" : "processing");
  const [pointsEarned, setPointsEarned] = useState(0);

  const finalizeOrder = async () => {
    if (!orderId) return;
    try {
      const { data } = await api.get(`/orders/${orderId}`);
      setPointsEarned(data.points_earned || 0);
    } catch { /* ignore */ }
    refreshUser();
  };

  useEffect(() => {
    if (cod) {
      clear();
      finalizeOrder();
      return;
    }
    if (!sessionId) return;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/orders/payment-status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          clear();
          finalizeOrder();
          return;
        }
        if (data.status === "expired" || data.payment_status === "expired") {
          setStatus("expired");
          return;
        }
        if (attempts < 8) setTimeout(poll, 2000);
        else setStatus("timeout");
      } catch {
        if (attempts < 8) setTimeout(poll, 2000);
        else setStatus("error");
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, cod]);

  return (
    <div className="max-w-2xl mx-auto px-6 py-24 text-center page-enter">
      {status === "processing" && (
        <>
          <Loader2 size={56} className="animate-spin text-[#D4AF37] mx-auto mb-6" />
          <h1 className="font-display text-3xl mb-3">{t("paymentProcessing")}</h1>
        </>
      )}
      {status === "paid" && (
        <>
          <div className="w-20 h-20 rounded-full bg-[#D4AF37]/10 border-2 border-[#D4AF37] mx-auto mb-6 flex items-center justify-center">
            <CheckCircle2 size={40} className="text-[#D4AF37]" />
          </div>
          <h1 className="font-display text-4xl mb-4" data-testid="order-success-title">{t("orderPlaced")}</h1>
          <p className="text-[#9CA3AF] mb-2">{t("thankYou")}</p>
          {pointsEarned > 0 && (
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-4 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/5 text-[#D4AF37] text-sm num-ltr" data-testid="order-points-earned">
              <Award size={16} /> {t("pointsEarned").replace("{n}", pointsEarned)}
            </div>
          )}
          {orderId && (
            <p className="text-sm text-[#9CA3AF] mb-8">
              {t("orderId")}: <span className="num-ltr text-[#D4AF37]" data-testid="order-id-display">{orderId.slice(0, 8)}…</span>
            </p>
          )}
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <Link to="/" data-testid="back-home-btn" className="btn-outline-gold px-6 py-3 rounded-full inline-block">
              {t("continueShopping")}
            </Link>
            <Link to="/my-orders" data-testid="view-orders-btn" className="btn-gold px-6 py-3 rounded-full inline-block">
              {t("viewMyOrders")}
            </Link>
          </div>
        </>
      )}
      {(status === "expired" || status === "error" || status === "timeout") && (
        <>
          <XCircle size={56} className="text-red-400 mx-auto mb-6" />
          <h1 className="font-display text-3xl mb-3">{t("paymentFailed")}</h1>
          <Link to="/checkout" data-testid="retry-checkout-btn" className="btn-gold mt-6 px-6 py-3 rounded-full inline-block">
            {t("checkout")}
          </Link>
        </>
      )}
    </div>
  );
}
